/**
 * Converters between Unity .previs format and web types.
 * Subset adapted from previs-pro-web.
 */

// ─── Color conversion ─────────────────────────────────────────────────────

export function colorIntToHex(color: number): string {
  if (color === 0) return '#000000';
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

// ─── Sensor size mapping ──────────────────────────────────────────────────

/**
 * Source of truth for the "Current enum names" section below is
 * Assets/Scripts/Document/DocumentSensorSize.cs — specifically each
 * entry's exact `displayName` string in `sensorSizeDefinitions`. If Unity
 * adds a new member to `DocumentSensorSize.SensorSizeType`, add a matching
 * entry here (and to the maintenance-tripwire test in
 * src/__tests__/converters.test.ts) or the UI will fall through to the
 * passthrough fallback and show the raw Unity identifier.
 */
export const SENSOR_SIZE_MAP: Record<string, string> = {
  // Legacy names (pre-refactor vocabulary; old .previs rows may still carry these)
  'FullFrame35mm': 'Full Frame 35mm',
  'Super35': 'Super 35',
  'APSCCanon': 'APS-C (Canon)',
  'APSCNikon': 'APS-C (Nikon)',
  'MicroFourThirds': 'Micro 4/3',
  'iPhone11': 'iPhone 11',
  'Standard16mm': '16mm',
  'Super16mm': 'Super 16mm',
  'AlexaLF': 'ALEXA LF',
  'TwentyThreeVideo': '2/3" Video',
  // Current enum names (DocumentSensorSize.SensorSizeType)
  'SensorSize_35mm': '35mm full frame',
  'SensorSize_iPhone11_Normal': 'iPhone 11',
  'SensorSize_Canon_APS_C': 'Canon APS-C',
  'SensorSize_Nikon_APS_C': 'Nikon APS-C',
  'SensorSize_Super35': 'Super 35',
  'SensorSize_Micro43': 'Micro 4/3',
  'SensorSize_23Video': '2/3 Video',
  'SensorSize_Alexa_LF_Open_Gate': 'Alexa LF Open Gate',
  'SensorSize_Standard16mm': 'Standard 16mm',
  'SensorSize_Super16mm': 'Super 16mm',
  'SensorSize_Alexa_65': 'Alexa 65',
  'SensorSize_Alexa_265': 'Alexa 265',
  'SensorSize_Blackmagic_URSA_Cine_17K_65': 'Blackmagic URSA Cine 17K 65',
};

export function sensorSizeToWeb(native: string): string {
  return SENSOR_SIZE_MAP[native] ?? (native || 'Full Frame 35mm');
}

// ─── Aspect ratio mapping ─────────────────────────────────────────────────

const ASPECT_RATIO_MAP: Record<string, string> = {
  // Legacy names
  'SixteenByNine': '16:9',
  'OnePointEightFiveByOne': '1.85:1',
  'TwoPointThreeFiveByOne': '2.35:1',
  'TwoPointThreeNineByOne': '2.39:1',
  'FourByThree': '4:3',
  'OneByOne': '1:1',
  'ThreeByTwo': '3:2',
  'NineBySixteen': '9:16',
  // Current enum names (DocumentAspectRatio.AspectRatioType)
  'AspectRatio16to9': '16:9',
  'AspectRatio185to100': '1.85:1',
  'AspectRatio235to100': '2.35:1',
  'AspectRatio239to100': '2.39:1',
  'AspectRatio3to2': '3:2',
  'AspectRatio4to3': '4:3',
  'AspectRatio1to1': '1:1',
  'AspectRatio18to9': '2:1',
  'AspectRatio5to3': '5:3',
  'AspectRatio143to100': '1.43:1',
  'AspectRatio189to100': '1.89:1',
  'AspectRatio9to16': '9:16',
  'AspectRatio1to2': '1:2',
};

export function aspectRatioToWeb(native: string): string {
  return ASPECT_RATIO_MAP[native] ?? (native || '16:9');
}

// ─── Location type mapping ────────────────────────────────────────────────

const LOCATION_TYPE_MAP: Record<string, string> = {
  'Interior': 'INT',
  'Exterior': 'EXT',
  'InteriorExterior': 'INT/EXT',
};

export function locationTypeToWeb(native: string): string {
  return LOCATION_TYPE_MAP[native] ?? (native || 'INT');
}

/** Reverse of locationTypeToWeb: "INT"/"EXT"/"INT/EXT" → Unity enum names. */
const LOCATION_TYPE_FROM_WEB: Record<string, string> = {
  'INT': 'Interior',
  'EXT': 'Exterior',
  'INT/EXT': 'InteriorExterior',
  'I/E': 'InteriorExterior',
};

/**
 * Convert a web-side abbreviation ("INT"/"EXT"/"INT/EXT") back to the
 * Unity `DocumentSceneLocation.SceneLocationType` enum name the scenes
 * table and the merge-plan DTO both expect. Returns "" for empty or
 * unknown input so downstream code can distinguish "unset" from a
 * miscast default.
 */
export function locationTypeToNative(web: string): string {
  const key = (web || '').trim().toUpperCase();
  if (!key) return '';
  return LOCATION_TYPE_FROM_WEB[key] ?? '';
}

// ─── Time of day mapping ──────────────────────────────────────────────────

const TIME_MAP: Record<string, string> = {
  'Day': 'DAY',
  'Night': 'NIGHT',
  'Twilight': 'TWILIGHT',
};

export function timeOfDayToWeb(native: string): string {
  return TIME_MAP[native] ?? (native || 'DAY');
}

/** Reverse of timeOfDayToWeb: "DAY"/"NIGHT"/etc → Unity enum names. */
const TIME_FROM_WEB: Record<string, string> = {
  'DAY': 'Day',
  'NIGHT': 'Night',
  'TWILIGHT': 'Twilight',
  'NOIR': 'Noir',
};

/**
 * Convert a web-side time-of-day label ("DAY"/"NIGHT"/etc) back to the
 * Unity `DocumentSceneTime.SceneTimeType` enum name. Returns "" when
 * empty or unknown so ApplySceneOps can skip writing the timetype
 * column rather than forcing a default like "Day".
 */
export function timeOfDayToNative(web: string): string {
  const key = (web || '').trim().toUpperCase();
  if (!key) return '';
  return TIME_FROM_WEB[key] ?? '';
}

// ─── Transition type mapping ─────────────────────────────────────────────

import type { TransitionType } from '@/types/timeline.ts';

const TRANSITION_TYPE_MAP: Record<string, TransitionType> = {
  'dissolve': 'dissolve',
  'Dissolve': 'dissolve',
  'CrossDissolve': 'dissolve',
  'fade_to_black': 'fade_to_black',
  'FadeToBlack': 'fade_to_black',
  'fade_from_black': 'fade_from_black',
  'FadeFromBlack': 'fade_from_black',
  'cut': 'cut',
  'Cut': 'cut',
  'continuous': 'continuous',
  'Continuous': 'continuous',
  'wipe_left': 'dissolve',    // approximate unsupported types
  'wipe_right': 'dissolve',
  'WipeLeft': 'dissolve',
  'WipeRight': 'dissolve',
};

export function transitionTypeToWeb(native: string): TransitionType {
  return TRANSITION_TYPE_MAP[native] ?? 'cut';
}

// ─── Shot size mapping ───────────────────────────────────────────────────

const SHOT_SIZE_MAP: Record<string, string> = {
  // Unity enum → web label (used in UI + prompt builder)
  'ExtremeCloseUpShot': 'ECU',
  'ExtremeCloseUp': 'ECU',
  'ChokerShot': 'Choker',
  'CloseUpShot': 'Close-Up',
  'CloseUp': 'Close-Up',
  'MediumCloseUpShot': 'Med CU',
  'MediumCloseUp': 'Med CU',
  'MediumShot': 'Medium',
  'Medium': 'Medium',
  'CowboyShot': 'Cowboy',
  'MediumFullShot': 'Med Full',
  'MediumWide': 'Med Wide',
  'FullShot': 'Full',
  'WideShot': 'Wide',
  'Wide': 'Wide',
  'LongShot': 'Long',
  'ExtemeWideShot': 'Extreme Wide',   // note: Unity typo "Exteme"
  'ExtremeWide': 'Extreme Wide',
  'ExtremeLongShot': 'Extreme Long',
  'EstablishingShot': 'Establishing',
  'AerialShot': 'Aerial',
};

export function shotSizeToWeb(native: string | null): string | undefined {
  if (!native) return undefined;
  return SHOT_SIZE_MAP[native] ?? native;
}

/**
 * Reverse of shotSizeToWeb: web display label (as offered by SHOT_SIZE_OPTIONS,
 * see src/previs/shotOptions.ts) → Unity `DocumentShotSize.ShotSizeType` enum
 * member name. Verified against Assets/Scripts/Document/DocumentShotSize.cs —
 * note the deliberate Unity typo "ExtemeWideShot" (missing 'r') for Extreme Wide.
 * Returns '' for a label with no corresponding enum member (Unity stores
 * Unspecified); every SHOT_SIZE_OPTIONS label currently has a real match.
 */
const SHOT_SIZE_TO_NATIVE: Record<string, string> = {
  'Aerial': 'AerialShot',
  'Establishing': 'EstablishingShot',
  'Extreme Long': 'ExtremeLongShot',
  'Extreme Wide': 'ExtemeWideShot',   // note: Unity typo "Exteme"
  'Long': 'LongShot',
  'Wide': 'WideShot',
  'Full': 'FullShot',
  'Med Full': 'MediumFullShot',
  'Cowboy': 'CowboyShot',
  'Medium': 'MediumShot',
  'Med CU': 'MediumCloseUpShot',
  'Close-Up': 'CloseUpShot',
  'Choker': 'ChokerShot',
  'ECU': 'ExtremeCloseUpShot',
};

export function shotSizeToNative(webLabel: string): string {
  return SHOT_SIZE_TO_NATIVE[webLabel] ?? '';
}

// ─── Shot angle mapping ──────────────────────────────────────────────────

const SHOT_ANGLE_MAP: Record<string, string> = {
  // Unity enum → web label
  'EyeLevel': 'Eye Level',
  'LowAngle': 'Low Angle',
  'HighAngle': 'High Angle',
  'HipLevel': 'Hip Level',
  'KneeLevel': 'Knee Level',
  'GroundLevel': 'Ground Level',
  'ShoulderLevel': 'Shoulder Level',
  'DutchAngle': 'Dutch',
  'Dutch': 'Dutch',
  'BirdsEyeView': "Bird's Eye",
  'BirdsEye': "Bird's Eye",
  'WormsEye': "Worm's Eye",
  'Overhead': 'Overhead',
  'Aerial': 'Aerial',
  'Helicopter': 'Helicopter',
  'Drone': 'Drone',
  'OverTheShoulder': 'OTS',
};

export function shotAngleToWeb(native: string | null): string | undefined {
  if (!native) return undefined;
  return SHOT_ANGLE_MAP[native] ?? native;
}

/**
 * Reverse of shotAngleToWeb: web display label (as offered by SHOT_ANGLE_OPTIONS,
 * see src/previs/shotOptions.ts) → Unity `DocumentShotAngle.ShotAngleType` enum
 * member name. Verified against Assets/Scripts/Document/DocumentShotAngle.cs —
 * note the bird's-eye member is `BirdsEyeView` (not "BirdsEye") and Dutch is
 * `DutchAngle` (not "Dutch"). Returns '' for a label with no corresponding enum
 * member (Unity stores Unspecified); every SHOT_ANGLE_OPTIONS label currently
 * has a real match.
 */
const SHOT_ANGLE_TO_NATIVE: Record<string, string> = {
  'Ground Level': 'GroundLevel',
  'Knee Level': 'KneeLevel',
  'Hip Level': 'HipLevel',
  'Eye Level': 'EyeLevel',
  'Shoulder Level': 'ShoulderLevel',
  'Low Angle': 'LowAngle',
  'High Angle': 'HighAngle',
  'Dutch': 'DutchAngle',
  'Overhead': 'Overhead',
  "Bird's Eye": 'BirdsEyeView',
  'Aerial': 'Aerial',
  'Helicopter': 'Helicopter',
  'Drone': 'Drone',
};

export function shotAngleToNative(webLabel: string): string {
  return SHOT_ANGLE_TO_NATIVE[webLabel] ?? '';
}

// ─── Stored multi-value Size / Angle ─────────────────────────────────────
//
// 20261005 - The native Shot Settings pickers for Size and Angle are multi-select
// (DocumentShotSize / DocumentShotAngle CanMultipleTypesBeSelected), stored space-separated:
// "MediumShot CloseUpShot". shotSizeToWeb / shotAngleToWeb miss on such a value and hand it
// through unchanged, and the *ToNative maps above answer '' for it. These say whether a value is
// made only of names the native-to-web map knows, so a caller can send it back as stored
// (toCoveragePayloadShot) instead of blanking the column. Own keys only: 'constructor' is not a name.

function isNativeValueList(value: string, nativeToWeb: Record<string, string>): boolean {
  const tokens = value.trim().split(/\s+/);
  return tokens[0] !== '' && tokens.every((token) => Object.prototype.hasOwnProperty.call(nativeToWeb, token));
}

export function isNativeShotSizeValue(value: string): boolean {
  return isNativeValueList(value, SHOT_SIZE_MAP);
}

export function isNativeShotAngleValue(value: string): boolean {
  return isNativeValueList(value, SHOT_ANGLE_MAP);
}

// ─── Shot framing mapping ───────────────────────────────────────────────

const SHOT_FRAMING_MAP: Record<string, string> = {
  'SingleShot': 'SS',
  'TwoShot': '2S',
  'ThreeShot': '3S',
  'FourShot': '4S',
  'FiveShot': '5S',
  'OverTheShoulderShot': 'OTS',
  'OverTheHipShot': 'OTH',
  'PointOfViewShot': 'POV',
};

const SHOT_FRAMING_REVERSE: Record<string, string> = {
  'SS': 'SingleShot',
  '2S': 'TwoShot',
  '3S': 'ThreeShot',
  '4S': 'FourShot',
  '5S': 'FiveShot',
  'OTS': 'OverTheShoulderShot',
  'OTH': 'OverTheHipShot',
  'POV': 'PointOfViewShot',
};

export function shotFramingToWeb(native: string | null): string | undefined {
  if (!native) return undefined;
  // shotframingtype can be space-separated: "SingleShot OverTheShoulderShot" → "SS OTS"
  return native.split(' ').filter(Boolean)
    .map(tok => SHOT_FRAMING_MAP[tok] ?? tok)
    .join(' ') || undefined;
}

export function shotFramingToNative(web: string | null): string | null {
  if (!web) return null;
  return web.split(' ').filter(Boolean)
    .map(tok => SHOT_FRAMING_REVERSE[tok] ?? tok)
    .join(' ') || null;
}

// ─── Shot movement mapping ───────────────────────────────────────────────

/** Convert native enum name to web display value: "DollyInShot" → "Dolly In" */
export function shotMovementToWeb(native: string | null): string | undefined {
  if (!native || native === 'Unspecified') return undefined;
  // Strip trailing "Shot" suffix, then split PascalCase into words
  const stripped = native.endsWith('Shot') ? native.slice(0, -4) : native;
  return stripped.replace(/([a-z])([A-Z])/g, '$1 $2');
}

/**
 * Convert web display value back to native enum name: "Dolly In" → "DollyInShot".
 * Verified against Assets/Scripts/Document/DocumentShotMovement.cs
 * (`ShotMovementType`): every label in SHOT_MOVEMENT_OPTIONS (src/previs/shotOptions.ts)
 * strips-spaces-and-appends-"Shot" to a real enum member name — e.g. 'Pan Left' →
 * 'PanLeftShot', 'Dolly Zoom' → 'DollyZoomShot', 'Whip Pan' → 'WhipPanShot' — so no
 * explicit lookup table is needed here (unlike shot size/angle, whose web labels
 * don't algorithmically match their enum names). '' (the "unset" option) maps to
 * the literal 'Unspecified' member rather than ''.
 */
export function shotMovementToNative(web: string): string {
  if (!web) return 'Unspecified';
  return web.replace(/ /g, '') + 'Shot';
}

// ─── Thumbnail conversion ─────────────────────────────────────────────────

export function blobToDataUrl(blob: Uint8Array | null): string | undefined {
  if (!blob || blob.length === 0) return undefined;
  const base64 = uint8ArrayToBase64(blob);
  return `data:image/jpeg;base64,${base64}`;
}

function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}
